/**
 * How long to keep steering before giving up, and how many frames at the target count as
 * having arrived.
 *
 * A single `scrollTo` is not enough on this page, because the destination moves after the
 * journey has started — in three separate ways:
 *
 *   1. Tiles are only drawn near the viewport, so passing a skeleton that becomes a taller
 *      product pushes everything below it, including whatever we were aiming at.
 *   2. The mobile category menu is a dialog, and a dialog holds a scroll lock until its
 *      closing transition has finished. A scroll issued while the lock is on does nothing
 *      at all — which is why the menu "sometimes scrolled and sometimes stopped half way".
 *   3. The mobile header collapses its search bar as soon as the page moves, so the chrome
 *      being cleared is 54px shorter at the end of the journey than it was at the start.
 *
 * Three frames rather than one because a smooth scroll can pass through the right value on
 * its way to the wrong one.
 */
const DEADLINE_MS = 2500;
const ARRIVED_FRAMES = 3;

/**
 * A correction longer than this is animated; anything shorter is snapped.
 *
 * The corrections are the point of the loop and most of them are a few pixels of settling.
 * Animating those reads as the page wobbling after it has stopped.
 */
const SMOOTH_ABOVE_PX = 80;

/**
 * A hair of daylight under the sticky chrome.
 *
 * Scroll positions are fractional and `getBoundingClientRect` is too, so landing a card
 * exactly on the boundary rounds under it about half the time and the customer sees a
 * clipped cover.
 *
 * Exported because the navbar has to agree with it: it lights the pill for the section
 * whose heading has reached its resting place, and a heading parked deliberately two
 * pixels short of that is still the section you are in.
 */
export const SCROLL_CLEARANCE_PX = 2;

export type SaleScrollRequest = {
  /** The element to bring into view — a `prod_…` tile or a `sect_wrapper…` section. */
  elementId: string;
  /** Which category it is in. Its heading is the thing that would otherwise cover it. */
  categoryId: number;
  /**
   * True when the target ends up *underneath* the sticky category heading rather than
   * being it.
   *
   * A section's own first child is its heading, so landing the section at the heading's
   * resting place is exactly right. A product tile has that heading stuck above it and has
   * to clear its height as well — miss this and the top of the card is behind the bar,
   * which is what it did on every scroll on a phone.
   */
  behindHeading: boolean;
  /** Called once the page has come to rest on the target, or the deadline has passed. */
  onSettled?: () => void;
};

/**
 * Scrolls to a place in the sale and keeps it there until the page settles.
 *
 * Returns a cancel function; calling it abandons the journey without settling, which is
 * what a second request (or an unmount) does to the first.
 */
export default function scrollToSaleTarget({
  elementId,
  categoryId,
  behindHeading,
  onSettled,
}: SaleScrollRequest): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  let frame = 0;
  let finished = false;
  let arrived = 0;
  let previousY = window.scrollY;

  const giveUpAt = Date.now() + DEADLINE_MS;

  const stop = (settled: boolean) => {
    if (finished) {
      return;
    }

    finished = true;
    cancelAnimationFrame(frame);
    window.removeEventListener('wheel', abandon);
    window.removeEventListener('touchstart', abandon);

    if (settled && onSettled) {
      onSettled();
    }
  };

  // A customer who has started scrolling themselves has said where they want to be, and a
  // loop that keeps correcting for another two seconds is fighting their finger.
  const abandon = () => stop(false);

  const step = () => {
    if (finished) {
      return;
    }

    const element = document.getElementById(elementId);

    // No element yet is a normal state, not a failure: the tile being scrolled to is
    // pinned but React has not drawn it, so this waits for it rather than giving up on it.
    if (element) {
      const target = scrollTopFor(element, categoryId, behindHeading);
      const distance = Math.abs(window.scrollY - target);

      if (distance <= 1) {
        arrived += 1;

        if (arrived >= ARRIVED_FRAMES) {
          stop(true);
          return;
        }
      } else {
        arrived = 0;

        // Only once the page has come to rest. Re-issuing mid-animation restarts the
        // scroll from wherever it had reached, which is how a smooth scroll turns into a
        // crawl that never arrives.
        if (window.scrollY === previousY) {
          window.scrollTo({ top: target, behavior: distance > SMOOTH_ABOVE_PX ? 'smooth' : 'auto' });
        }
      }
    }

    previousY = window.scrollY;

    if (Date.now() >= giveUpAt) {
      // Settled by exhaustion rather than by arrival. It still counts as arriving for the
      // caller's purposes — a search that scrolled imperfectly should still open the
      // product it was asked for.
      stop(true);
      return;
    }

    frame = requestAnimationFrame(step);
  };

  window.addEventListener('wheel', abandon, { passive: true });
  window.addEventListener('touchstart', abandon, { passive: true });

  step();

  return () => stop(false);
}

/**
 * Where the page has to be for this element to sit just below the sticky chrome.
 *
 * Clamped to what the document can actually offer: the last product of the last category
 * cannot be brought to the top of the viewport, and without the clamp the loop would spend
 * its whole deadline asking for a position the browser will never report back.
 */
function scrollTopFor(element: HTMLElement, categoryId: number, behindHeading: boolean): number {
  const wanted =
    element.getBoundingClientRect().top +
    window.scrollY -
    stickyChromeHeight(categoryId, behindHeading) -
    SCROLL_CLEARANCE_PX;

  const furthest = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

  return Math.min(Math.max(0, wanted), furthest);
}

/**
 * How much of the top of the screen is covered by things that do not scroll.
 *
 * Asked of the layout rather than restated here. The category heading declares its own
 * resting place as `top: calc(var(--sale-header-height) + var(--sale-nav-height))`, both of
 * which are measured — the header is three heights across the breakpoints and the pill bar
 * is a fourth that becomes zero on a phone — so the browser's resolved value for that one
 * declaration is the whole answer, and a sum written out again here would drift from it.
 *
 * The custom properties are the fallback for a target with no heading of its own.
 */
function stickyChromeHeight(categoryId: number, behindHeading: boolean): number {
  const heading = document.getElementById(`sect${categoryId}`);

  if (heading) {
    const restingPlace = parseFloat(getComputedStyle(heading).top) || 0;

    return restingPlace + (behindHeading ? heading.offsetHeight : 0);
  }

  return saleChromeHeight();
}

/**
 * The fixed header and the category navbar together, as the page publishes them: where a
 * section's heading would come to rest, for a section that has none.
 *
 * A section of postcards is one — it opens on an explainer that scrolls away — and the
 * navbar lights its pill by this too, which is why it is exported.
 */
export function saleChromeHeight(): number {
  const root = getComputedStyle(document.documentElement);

  return (
    (parseFloat(root.getPropertyValue('--sale-header-height')) || 0) +
    (parseFloat(root.getPropertyValue('--sale-nav-height')) || 0)
  );
}
